<template>
  <div class="asystent">
    <v-card outlined class="mb-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-text class="d-flex align-center" style="gap: 18px">
        <!-- Ten sam robot, którego kierowcy znają z aplikacji — jedna twarz
             asystenta w całym systemie. Macha na powitanie, smutnieje, gdy
             dane się nie zgadzają. -->
        <AssistantRobot
          :size="96"
          :anim="robotAnim"
          greet
          ref="robot"
          style="flex: 0 0 auto"
        />
        <div>
          <div class="text-h6 white--text">Asystent biura</div>
          <div class="grey--text" style="font-size: 13.5px; line-height: 1.5">
            Pyta bazę i odpowiada, skąd biorą się kwoty. Wszystkie liczby pochodzą z tych samych
            wyliczeń co panel — asystent ich nie wymyśla. Gdy dane się nie zgadzają, powie o tym
            wprost zamiast tłumaczyć.
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-alert v-if="niedostepny" type="warning" text dense>
      Asystent jest wyłączony — brakuje klucza do usługi AI.
    </v-alert>

    <div v-if="!rozmowa.length" class="podpowiedzi">
      <div class="podpowiedzi__tytul">O co możesz zapytać:</div>
      <v-chip v-for="(p, i) in przyklady" :key="i" class="ma-1" small outlined @click="zapytaj(p)">
        {{ p }}
      </v-chip>
    </div>

    <div v-for="(w, i) in rozmowa" :key="i" class="wpis">
      <div class="wpis__pytanie">
        <v-icon size="18" class="mr-1">mdi-account</v-icon>{{ w.pytanie }}
      </div>
      <div v-if="w.trwa" class="wpis__odpowiedz wpis__odpowiedz--czeka">
        <v-progress-circular indeterminate size="16" width="2" class="mr-2" />sprawdzam w bazie…
      </div>
      <div v-else class="wpis__odpowiedz">
        <div v-if="w.niespojnosci && w.niespojnosci.length" class="wpis__alarm">
          <v-icon size="18" color="error" class="mr-1">mdi-alert-circle</v-icon>
          <div>
            <strong>To wygląda na błąd systemu, nie na zwykłe potrącenie:</strong>
            <div v-for="(n, j) in w.niespojnosci" :key="j">{{ n }}</div>
          </div>
        </div>
        <div class="wpis__tekst" v-html="naHtml(w.odpowiedz)"></div>
        <div v-if="w.narzedzia && w.narzedzia.length" class="wpis__zrodla">
          sprawdzone: {{ [...new Set(w.narzedzia)].join(', ').replace(/_/g, ' ') }}
        </div>
      </div>
    </div>

    <v-card outlined class="mt-4" style="background: var(--bg-surface); border-color: var(--border-default);">
      <v-card-text class="d-flex align-center" style="gap: 12px">
        <v-text-field
          v-model="pytanie"
          placeholder="np. czemu Kowalski dostał 0 zł za ostatni tydzień?"
          outlined dense hide-details dark
          :disabled="czeka || niedostepny"
          @keyup.enter="zapytaj()"
        />
        <v-btn color="primary" :loading="czeka" :disabled="!pytanie.trim() || niedostepny" @click="zapytaj()">
          <v-icon left>mdi-send</v-icon> Zapytaj
        </v-btn>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios';
import AssistantRobot from '@/components/AssistantRobot.vue';

export default {
  name: 'AssistantPage',
  components: { AssistantRobot },
  data() {
    return {
      robotAnim: 'Idle',
      pytanie: '',
      czeka: false,
      niedostepny: false,
      rozmowa: [],
      przyklady: [
        'Ile jest teraz w kasie w Bydgoszczy?',
        'Czemu Klich nie dostał wypłaty?',
        'Jakie potrącenia ma Grzeszczak w tym tygodniu?',
        'Które faktury Wiśniewskiego czekają na sprawdzenie?',
      ],
    };
  },
  created() {
    this.sprawdzDostepnosc();
  },
  methods: {
    naglowki() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    /** Zamiana **pogrubień** i nowych linii na HTML — model odpowiada markdownem. */
    naHtml(tekst) {
      const bezTagow = String(tekst || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return bezTagow
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>');
    },
    async sprawdzDostepnosc() {
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'assistant/status', { headers: this.naglowki() });
        this.niedostepny = !r.data?.data?.dostepny;
      } catch (e) {
        this.niedostepny = true;
      }
    },
    async zapytaj(gotowe) {
      const tresc = (gotowe || this.pytanie).trim();
      if (!tresc || this.czeka) return;
      this.pytanie = '';
      this.czeka = true;
      // Rozmowa SPRZED tego pytania — żeby asystent wiedział, o kim mowa,
      // gdy pracownik pisze dalej „on", „ten kierowca" albo samo nazwisko.
      const historia = [];
      this.rozmowa.forEach((w) => {
        if (w.pytanie) historia.push({ rola: 'user', tresc: w.pytanie });
        if (w.odpowiedz) historia.push({ rola: 'assistant', tresc: w.odpowiedz });
      });
      const wpis = { pytanie: tresc, odpowiedz: '', narzedzia: [], niespojnosci: [], trwa: true };
      this.rozmowa.push(wpis);
      try {
        const r = await axios.post(
          process.env.VUE_APP_ROOT_API + 'assistant/ask',
          { pytanie: tresc, historia },
          { headers: this.naglowki() },
        );
        const d = r.data?.data || {};
        wpis.odpowiedz = d.odpowiedz || 'Brak odpowiedzi.';
        wpis.narzedzia = d.uzyteNarzedzia || [];
        wpis.niespojnosci = d.niespojnosci || [];
        // Robot pokazuje, że coś jest nie tak, zanim pracownik przeczyta treść.
        this.robotAnim = wpis.niespojnosci.length ? 'Sad' : 'Idle';
      } catch (e) {
        wpis.odpowiedz = e.response?.data?.message || 'Nie udało się uzyskać odpowiedzi.';
        this.robotAnim = 'Sad';
      } finally {
        wpis.trwa = false;
        this.czeka = false;
        this.$nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }));
      }
    },
  },
};
</script>

<style scoped>
.asystent { padding: 16px; max-width: 900px; margin: 0 auto; }
.podpowiedzi { margin: 12px 0 20px; }
.podpowiedzi__tytul { font-size: 13px; opacity: 0.7; margin-bottom: 6px; }
.wpis { margin-bottom: 18px; }
.wpis__pytanie {
  font-weight: 600;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}
.wpis__odpowiedz {
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--border-default);
  line-height: 1.55;
}
.wpis__odpowiedz--czeka { display: flex; align-items: center; opacity: 0.7; }
.wpis__alarm {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border-radius: 8px;
  background: rgba(239, 83, 80, 0.12);
  border: 1px solid rgba(239, 83, 80, 0.4);
  font-size: 13.5px;
}
.wpis__zrodla { margin-top: 10px; font-size: 12px; opacity: 0.55; }
</style>
